import * as Sentry from '@sentry/react'
import { create } from 'zustand'
import { trpcClient } from '../../../../utils/trpc'
import type {
	AvailableField,
	ComponentLabel,
	CycleSummary,
	DistributionReportResult,
	ExportFormat,
	FormField,
	FormSummary,
	PivotReportResult,
	RawDataReportResult,
	RepeatableComponent,
	ReportBuilderConfig,
	ReportResult,
} from '../types'

// Re-export types for consumers
export type {
	AggregateOption,
	AvailableField,
	ComponentLabel,
	CycleSummary,
	ExportFormat,
	FilterCondition,
	FormField,
	FormSummary,
	PivotReportResult,
	RawDataColumn,
	RawDataReportResult,
	RawDataRow,
	RepeatableChildComponent,
	RepeatableComponent,
	ReportBuilderConfig,
	ReportResult,
	ReportRow,
	ReportType,
	SubjectType,
	UserAttributeColumn,
	UserAttributeSource,
	VisualizationType,
} from '../types'
export { OPERATORS_BY_TYPE, STANDARD_USER_ATTRIBUTES } from '../types'

interface ReportBuilderState {
	currentStep: number
	validationErrors: Record<string, string>

	forms: FormSummary[]
	cycles: CycleSummary[]
	availableCustomFields: string[]
	availableFields: AvailableField[]
	savedConfigs: ReportBuilderConfig[]
	actions: string[]
	repeatableComponents: RepeatableComponent[]
	// V2.5.2: Form fields for dynamic filtering
	formFields: FormField[]
	// V3: Component labels for grouping
	componentLabels: ComponentLabel[]
	availableGroupCategories: string[]

	currentConfig: ReportBuilderConfig

	isInitializing: boolean
	isGenerating: boolean
	isSaving: boolean
	isExporting: boolean
	isLoadingRepeatables: boolean
	// V2.5.2: Loading state for form fields
	isLoadingFormFields: boolean
	// V3: Loading state for component labels
	isLoadingComponentLabels: boolean
	reportResult: ReportResult | null
	pivotResult: PivotReportResult | null
	rawDataResult: RawDataReportResult | null
	// V3.3: Distribution report result
	distributionResult: DistributionReportResult | null
	error: string | null

	initialize: () => Promise<void>
	setCurrentStep: (step: number) => void
	nextStep: () => void
	prevStep: () => void

	updateConfig: (updates: Partial<ReportBuilderConfig>) => void
	resetConfig: () => void
	loadConfig: (config: ReportBuilderConfig) => Promise<void>

	generateReport: () => Promise<void>
	generateRawDataReport: () => Promise<void>
	// V3.3: Generate distribution report
	generateDistributionReport: () => Promise<void>
	loadRepeatableComponents: (formIds: string[]) => Promise<void>
	// V2.5.2: Load form fields for filtering
	loadFormFields: (formIds: string[]) => Promise<void>
	// V3: Component label management
	loadComponentLabels: (formIds: string[]) => Promise<void>
	setComponentLabel: (componentId: string, groupCategory: string) => Promise<void>
	deleteComponentLabel: (componentId: string) => Promise<void>
	loadGroupCategories: () => Promise<void>
	saveConfig: () => Promise<void>
	deleteConfig: (configId: string) => Promise<void>
	exportReport: (format: ExportFormat) => Promise<void>
	exportRawDataReport: (format: ExportFormat) => Promise<void>
	exportPivotReport: (format: ExportFormat) => Promise<void>
	exportDistributionReport: (format: ExportFormat) => Promise<void>

	setError: (error: string | null) => void
	clearError: () => void
}

function downloadBase64File(data: string, contentType: string, filename: string): void {
	const binaryString = atob(data)
	const bytes = new Uint8Array(binaryString.length)
	for (let i = 0; i < binaryString.length; i++) {
		bytes[i] = binaryString.charCodeAt(i)
	}
	const blob = new Blob([bytes], { type: contentType })
	const url = URL.createObjectURL(blob)
	const link = document.createElement('a')
	link.href = url
	link.download = filename
	document.body.appendChild(link)
	link.click()
	document.body.removeChild(link)
	URL.revokeObjectURL(url)
}

const DEFAULT_CONFIG: ReportBuilderConfig = {
	name: '',
	description: '',
	reportType: 'form',
	selectedFormIds: [],
	selectedCycleIds: [],
	selectedRepeatableIds: [],
	selectedUserAttributes: [
		{ field: 'email', source: 'participant', label: 'Participant Email' },
		{ field: 'department', source: 'participant', label: 'Department' },
		{ field: 'title', source: 'participant', label: 'Title' },
	],
	groupBy: ['Title'],
	groupBySource: 'custom_field',
	groupByPeriod: undefined,
	// V2: Multi-level row grouping
	rowFields: [],
	showSubtotals: false,
	// V2.5: Column axis for cross-tabulation
	columnFields: [],
	valueConfigs: [],
	aggregateBy: ['competency'],
	filters: [],
	filterLogic: 'AND',
	questionLabelMappings: {},
	visualizationType: 'table',
	exportFormat: 'xlsx',
}

export const useReportBuilderStore = create<ReportBuilderState>((set, get) => ({
	currentStep: 1,
	validationErrors: {},

	forms: [],
	cycles: [],
	availableCustomFields: [],
	availableFields: [],
	savedConfigs: [],
	actions: [],
	formFields: [],
	repeatableComponents: [],
	componentLabels: [],
	availableGroupCategories: [],

	currentConfig: { ...DEFAULT_CONFIG },

	isInitializing: false,
	isGenerating: false,
	isSaving: false,
	isExporting: false,
	isLoadingRepeatables: false,
	isLoadingFormFields: false,
	isLoadingComponentLabels: false,
	reportResult: null,
	pivotResult: null,
	rawDataResult: null,
	distributionResult: null,
	error: null,

	initialize: async () => {
		set({ isInitializing: true, error: null })
		try {
			const data = await trpcClient.client.reportBuilder.initialize.query()
			set({
				forms: data.forms,
				cycles: data.cycles,
				availableCustomFields: data.availableCustomFields,
				availableFields: data.availableFields,
				savedConfigs: data.savedConfigs,
				actions: data.actions,
				isInitializing: false,
			})
		} catch (error) {
			const isForbidden =
				error &&
				typeof error === 'object' &&
				'data' in error &&
				(error as { data?: { code?: string } }).data?.code === 'FORBIDDEN'
			if (!isForbidden) {
				Sentry.captureException(error)
			}
			set({
				error: isForbidden
					? 'You do not have admin access to view reports. Contact your cycle administrator.'
					: error instanceof Error
						? error.message
						: 'Failed to initialize report builder',
				isInitializing: false,
			})
			throw error
		}
	},

	setCurrentStep: (step) => set({ currentStep: step }),

	nextStep: () => {
		const { currentStep } = get()
		if (currentStep < 5) {
			set({ currentStep: currentStep + 1 })
		}
	},

	prevStep: () => {
		const { currentStep } = get()
		if (currentStep > 1) {
			set({ currentStep: currentStep - 1 })
		}
	},

	updateConfig: (updates) => {
		const { currentConfig } = get()
		set({
			currentConfig: { ...currentConfig, ...updates },
			validationErrors: {},
		})
	},

	resetConfig: () => {
		set({
			currentConfig: { ...DEFAULT_CONFIG },
			currentStep: 1,
			reportResult: null,
			pivotResult: null,
			rawDataResult: null,
			distributionResult: null,
			validationErrors: {},
		})
	},

	loadConfig: async (config) => {
		if (get().isGenerating) return
		set({
			currentConfig: { ...config },
			reportResult: null,
			pivotResult: null,
			rawDataResult: null,
			distributionResult: null,
			validationErrors: {},
			isGenerating: true,
			error: null,
		})

		try {
			// V3.3.1: Detect report type and call appropriate endpoint
			const isPivotMode = config.columnFields && config.columnFields.length > 0
			const isDistributionMode = config.reportType === 'distribution'
			const isRawDataMode = config.reportType === 'raw-data'

			if (isDistributionMode && config.distributionComponentId) {
				const result = await trpcClient.client.reportBuilder.generateDistributionReport.mutate({
					componentId: config.distributionComponentId,
					formIds: config.selectedFormIds,
					cycleIds: config.selectedCycleIds,
					filters: config.filters,
					filterLogic: config.filterLogic,
				})
				set({
					distributionResult: result,
					isGenerating: false,
					currentStep: 5,
				})
			} else if (isRawDataMode && config.selectedRepeatableIds?.length) {
				const result = await trpcClient.client.reportBuilder.generateRawDataReport.mutate({
					repeatableIds: config.selectedRepeatableIds,
					formIds: config.selectedFormIds,
					cycleIds: config.selectedCycleIds,
					filters: config.filters,
					filterLogic: config.filterLogic || 'AND',
				})
				set({
					rawDataResult: result,
					isGenerating: false,
					currentStep: 5,
				})
			} else if (isPivotMode) {
				const pivotResult = await trpcClient.client.reportBuilder.generatePivotReport.mutate({
					config,
				})
				set({
					pivotResult,
					isGenerating: false,
					currentStep: 5,
				})
			} else {
				const result = await trpcClient.client.reportBuilder.generateReport.mutate({
					config,
				})
				set({
					reportResult: result,
					isGenerating: false,
					currentStep: 5,
				})
			}
		} catch (error) {
			Sentry.captureException(error)
			set({
				error: error instanceof Error ? error.message : 'Failed to load report',
				isGenerating: false,
				currentStep: 1,
			})
		}
	},

	generateReport: async () => {
		if (get().isGenerating) return
		const { currentConfig } = get()

		const errors: Record<string, string> = {}

		// V3.3: Handle distribution report validation
		if (currentConfig.reportType === 'distribution') {
			if (!currentConfig.distributionComponentId) {
				errors.distribution = 'Please select a component for distribution analysis'
			}
			if (currentConfig.selectedFormIds.length === 0) {
				errors.selection = 'Please select at least one form for distribution reports'
			}
		} else if (currentConfig.reportType === 'form') {
			if (currentConfig.selectedFormIds.length === 0) {
				errors.selection = 'Please select at least one form for "By Form" reports'
			}
		} else if (currentConfig.reportType === 'cycle') {
			if (currentConfig.selectedCycleIds.length === 0) {
				errors.selection = 'Please select at least one cycle for "By Cycle" reports'
			}
		} else if (currentConfig.reportType === 'cross-cycle') {
			if (currentConfig.selectedCycleIds.length < 2) {
				errors.selection = 'Please select at least 2 cycles for "Cross-Cycle" comparison'
			}
		}

		// V3.3: Skip aggregation validation for distribution reports
		if (currentConfig.reportType !== 'distribution' && currentConfig.aggregateBy.length === 0) {
			errors.aggregateBy = 'Please select at least one aggregation option'
		}

		if (Object.keys(errors).length > 0) {
			set({ validationErrors: errors })
			return
		}

		set({ isGenerating: true, error: null, validationErrors: {} })
		try {
			// V3.3: Check for distribution mode first
			if (currentConfig.reportType === 'distribution' && currentConfig.distributionComponentId) {
				const result = await trpcClient.client.reportBuilder.generateDistributionReport.mutate({
					componentId: currentConfig.distributionComponentId,
					formIds: currentConfig.selectedFormIds,
					cycleIds: currentConfig.selectedCycleIds,
					filters: currentConfig.filters,
					filterLogic: currentConfig.filterLogic,
				})
				set({
					distributionResult: result,
					reportResult: null,
					pivotResult: null,
					rawDataResult: null,
					isGenerating: false,
					currentStep: 5,
				})
				return
			}

			// V2.5: Detect pivot mode and call the appropriate endpoint
			const isPivotMode = currentConfig.columnFields && currentConfig.columnFields.length > 0

			if (isPivotMode) {
				const pivotResult = await trpcClient.client.reportBuilder.generatePivotReport.mutate({
					config: currentConfig,
				})
				set({
					pivotResult,
					reportResult: null,
					distributionResult: null,
					rawDataResult: null,
					isGenerating: false,
					currentStep: 5,
				})
			} else {
				const result = await trpcClient.client.reportBuilder.generateReport.mutate({
					config: currentConfig,
				})
				set({
					reportResult: result,
					pivotResult: null,
					distributionResult: null,
					rawDataResult: null,
					isGenerating: false,
					currentStep: 5,
				})
			}
		} catch (error) {
			Sentry.captureException(error)
			set({
				error: error instanceof Error ? error.message : 'Failed to generate report',
				isGenerating: false,
			})
		}
	},

	generateRawDataReport: async () => {
		if (get().isGenerating) return
		const { currentConfig } = get()

		const errors: Record<string, string> = {}

		if (!currentConfig.selectedRepeatableIds || currentConfig.selectedRepeatableIds.length === 0) {
			errors.selection = 'Please select at least one repeatable component'
		}

		if (Object.keys(errors).length > 0) {
			set({ validationErrors: errors })
			return
		}

		set({ isGenerating: true, error: null, validationErrors: {} })
		try {
			const result = await trpcClient.client.reportBuilder.generateRawDataReport.mutate({
				repeatableIds: currentConfig.selectedRepeatableIds || [],
				formIds: currentConfig.selectedFormIds,
				cycleIds: currentConfig.selectedCycleIds,
				filters: currentConfig.filters,
				filterLogic: currentConfig.filterLogic || 'AND',
			})
			set({
				rawDataResult: result,
				isGenerating: false,
				currentStep: 5,
			})
		} catch (error) {
			Sentry.captureException(error)
			set({
				error: error instanceof Error ? error.message : 'Failed to generate raw data report',
				isGenerating: false,
			})
		}
	},

	generateDistributionReport: async () => {
		if (get().isGenerating) return
		const { currentConfig } = get()

		const errors: Record<string, string> = {}

		if (!currentConfig.distributionComponentId) {
			errors.distribution = 'Please select a component for distribution analysis'
		}

		if (currentConfig.selectedFormIds.length === 0) {
			errors.selection = 'Please select at least one form'
		}

		if (Object.keys(errors).length > 0) {
			set({ validationErrors: errors })
			return
		}

		set({ isGenerating: true, error: null, validationErrors: {} })
		try {
			const result = await trpcClient.client.reportBuilder.generateDistributionReport.mutate({
				componentId: currentConfig.distributionComponentId as string,
				formIds: currentConfig.selectedFormIds,
				cycleIds: currentConfig.selectedCycleIds,
				filters: currentConfig.filters,
				filterLogic: currentConfig.filterLogic,
			})
			set({
				distributionResult: result,
				reportResult: null,
				pivotResult: null,
				rawDataResult: null,
				isGenerating: false,
				currentStep: 5,
			})
		} catch (error) {
			Sentry.captureException(error)
			set({
				error: error instanceof Error ? error.message : 'Failed to generate distribution report',
				isGenerating: false,
			})
		}
	},

	loadRepeatableComponents: async (formIds: string[]) => {
		if (formIds.length === 0) {
			set({ repeatableComponents: [] })
			return
		}

		set({ isLoadingRepeatables: true, error: null })
		try {
			const result = await trpcClient.client.reportBuilder.getRepeatableComponents.query({
				formIds,
			})
			set({
				repeatableComponents: result,
				isLoadingRepeatables: false,
			})
		} catch (error) {
			Sentry.captureException(error)
			set({
				error: error instanceof Error ? error.message : 'Failed to load repeatable components',
				isLoadingRepeatables: false,
			})
		}
	},

	// V2.5.2: Load form fields for dynamic filtering
	loadFormFields: async (formIds: string[]) => {
		if (formIds.length === 0) {
			set({ formFields: [] })
			return
		}

		set({ isLoadingFormFields: true, error: null })
		try {
			const result = await trpcClient.client.reportBuilder.getFormFields.query({
				formIds,
			})
			set({
				formFields: result,
				isLoadingFormFields: false,
			})
		} catch (error) {
			Sentry.captureException(error)
			set({
				error: error instanceof Error ? error.message : 'Failed to load form fields',
				isLoadingFormFields: false,
			})
		}
	},

	// V3: Load component labels for grouping
	loadComponentLabels: async (formIds: string[]) => {
		if (formIds.length === 0) {
			set({ componentLabels: [] })
			return
		}

		set({ isLoadingComponentLabels: true, error: null })
		try {
			const result = await trpcClient.client.reportBuilder.getComponentLabels.query({
				formIds,
			})
			set({
				componentLabels: result,
				isLoadingComponentLabels: false,
			})
		} catch (error) {
			Sentry.captureException(error)
			set({
				error: error instanceof Error ? error.message : 'Failed to load component labels',
				isLoadingComponentLabels: false,
			})
		}
	},

	setComponentLabel: async (componentId: string, groupCategory: string) => {
		const previousLabels = get().componentLabels
		set({
			componentLabels: previousLabels.map((cl) => (cl.componentId === componentId ? { ...cl, groupCategory } : cl)),
		})
		try {
			await trpcClient.client.reportBuilder.setComponentLabel.mutate({
				componentId,
				groupCategory,
			})
			await get().loadGroupCategories()
		} catch (error) {
			Sentry.captureException(error)
			set({
				componentLabels: previousLabels,
				error: error instanceof Error ? error.message : 'Failed to set component label',
			})
		}
	},

	deleteComponentLabel: async (componentId: string) => {
		const previousLabels = get().componentLabels
		set({
			componentLabels: previousLabels.map((cl) =>
				cl.componentId === componentId ? { ...cl, groupCategory: null } : cl,
			),
		})
		try {
			await trpcClient.client.reportBuilder.deleteComponentLabel.mutate({
				componentId,
			})
			await get().loadGroupCategories()
		} catch (error) {
			Sentry.captureException(error)
			set({
				componentLabels: previousLabels,
				error: error instanceof Error ? error.message : 'Failed to delete component label',
			})
		}
	},

	loadGroupCategories: async () => {
		try {
			const result = await trpcClient.client.reportBuilder.getDistinctGroupCategories.query()
			set({ availableGroupCategories: result })
		} catch (error) {
			Sentry.captureException(error)
			set({
				error: error instanceof Error ? error.message : 'Failed to load group categories',
			})
		}
	},

	saveConfig: async () => {
		const { currentConfig } = get()

		if (!currentConfig.name.trim()) {
			set({ validationErrors: { name: 'Please enter a name for the report configuration' } })
			return
		}

		set({ isSaving: true, error: null })
		try {
			const result = await trpcClient.client.reportBuilder.saveConfig.mutate(currentConfig)
			set({
				savedConfigs: result.allConfigs,
				currentConfig: result.savedConfig,
				isSaving: false,
			})
		} catch (error) {
			Sentry.captureException(error)
			set({
				error: error instanceof Error ? error.message : 'Failed to save configuration',
				isSaving: false,
			})
		}
	},

	deleteConfig: async (configId) => {
		const { currentConfig } = get()
		set({ error: null, isSaving: true })
		try {
			const result = await trpcClient.client.reportBuilder.deleteConfig.mutate({ configId })
			const updates: Partial<ReportBuilderState> = {
				savedConfigs: result.allConfigs,
				isSaving: false,
			}
			if (currentConfig.id === configId) {
				updates.currentConfig = { ...currentConfig, id: undefined }
			}
			set(updates)
		} catch (error) {
			Sentry.captureException(error)
			set({
				error: error instanceof Error ? error.message : 'Failed to delete configuration',
				isSaving: false,
			})
		}
	},

	exportReport: async (format) => {
		const { reportResult, currentConfig } = get()
		if (!reportResult) {
			set({ error: 'No report data to export' })
			return
		}

		set({ isExporting: true, error: null })
		try {
			const result = await trpcClient.client.reportBuilder.exportReport.mutate({
				reportData: reportResult.data,
				format,
				configName: currentConfig.name || undefined,
				// V2.1: Pass columns from metadata (SINGLE SOURCE OF TRUTH)
				columns: reportResult.metadata.columns,
			})

			downloadBase64File(result.data, result.contentType, result.filename)
			set({ isExporting: false })
		} catch (error) {
			Sentry.captureException(error)
			set({
				error: error instanceof Error ? error.message : 'Failed to export report',
				isExporting: false,
			})
		}
	},

	exportRawDataReport: async (format) => {
		const { rawDataResult, currentConfig } = get()
		if (!rawDataResult) {
			set({ error: 'No raw data report to export' })
			return
		}

		set({ isExporting: true, error: null })
		try {
			const result = await trpcClient.client.reportBuilder.exportRawDataReport.mutate({
				reportData: rawDataResult.data,
				columns: rawDataResult.metadata.columns.map((c) => ({ id: c.id, label: c.label })),
				format,
				configName: currentConfig.name || undefined,
			})

			downloadBase64File(result.data, result.contentType, result.filename)
			set({ isExporting: false })
		} catch (error) {
			Sentry.captureException(error)
			set({
				error: error instanceof Error ? error.message : 'Failed to export raw data report',
				isExporting: false,
			})
		}
	},

	exportPivotReport: async (format) => {
		const { pivotResult, currentConfig } = get()
		if (!pivotResult) {
			set({ error: 'No pivot report to export' })
			return
		}

		set({ isExporting: true, error: null })
		try {
			const result = await trpcClient.client.reportBuilder.exportPivotReport.mutate({
				pivotResult,
				format,
				configName: currentConfig.name || undefined,
			})

			downloadBase64File(result.data, result.contentType, result.filename)
			set({ isExporting: false })
		} catch (error) {
			Sentry.captureException(error)
			set({
				error: error instanceof Error ? error.message : 'Failed to export pivot report',
				isExporting: false,
			})
		}
	},

	exportDistributionReport: async (format) => {
		const { distributionResult, currentConfig } = get()
		if (!distributionResult) {
			set({ error: 'No distribution report to export' })
			return
		}

		set({ isExporting: true, error: null })
		try {
			const reportData = distributionResult.distribution.map((row) => ({
				participantId: '',
				formId: '',
				formName: distributionResult.responseFieldLabel,
				competency: row.responseValue,
				count: row.count,
				averageRating: row.percentage,
				groupKey: row.responseValue,
			}))

			const columns = [
				{
					key: 'competency',
					header: 'Response Value',
					align: 'left' as const,
					format: 'text' as const,
				},
				{ key: 'count', header: 'Count', align: 'right' as const, format: 'number' as const },
				{
					key: 'averageRating',
					header: 'Percentage',
					align: 'right' as const,
					format: 'number' as const,
				},
			]

			const result = await trpcClient.client.reportBuilder.exportReport.mutate({
				reportData,
				format,
				configName: currentConfig.name ? `${currentConfig.name}-distribution` : 'distribution-report',
				columns,
			})

			downloadBase64File(result.data, result.contentType, result.filename)
			set({ isExporting: false })
		} catch (error) {
			Sentry.captureException(error)
			set({
				error: error instanceof Error ? error.message : 'Failed to export distribution report',
				isExporting: false,
			})
		}
	},

	setError: (error) => set({ error }),
	clearError: () => set({ error: null }),
}))
